import { LogarithmicOptions, SharesInputs } from './types'
import { PRICE_PER_SHARE, WITH_BOLT_ADVANTAGE } from './constants'

/**
 * Calculate the expected return on investment
 * @param {number} totalPrice
 * @returns {Pick<SharesInputs, "expectedReturnPlentyOnly" | "expectedReturnPlentyWithBolt">}
 */
export const calculateReturnOnInvestment = (
  totalPrice: number
): Pick<SharesInputs, 'expectedReturnPlentyOnly' | 'expectedReturnPlentyWithBolt'> => {
  return {
    expectedReturnPlentyOnly: totalPrice * 0.06,
    expectedReturnPlentyWithBolt: totalPrice * 0.06 + WITH_BOLT_ADVANTAGE
  }
}

/**
 * Calculate the shares inputs
 * @param {number} shares
 * @returns {SharesInputs}
 */
export const calculateInputs = (shares: number): SharesInputs => {
  const totalPrice = shares * PRICE_PER_SHARE
  const ROI = calculateReturnOnInvestment(totalPrice)

  return {
    shares,
    totalPrice,
    ...ROI
  }
}

/**
 * Convert the slider non-logarithmic slider position to a logarithmic value
 * @param {number} position
 * @param {LogarithmicOptions} sliderOptions
 * @param {LogarithmicOptions} logarithmicOptions
 * @returns {number}
 */
export const calculateLogarithmicValue = (
  position: number,
  sliderOptions: LogarithmicOptions,
  logarithmicOptions?: LogarithmicOptions
): number => {
  const logarithmicMinValue = Math.log(logarithmicOptions?.min || 1)
  const logarithmicMaxValue = Math.log(logarithmicOptions?.max || 1000)

  const scale = (logarithmicMaxValue - logarithmicMinValue) / (sliderOptions.max - sliderOptions.min)
  return Math.round(Math.exp((position - sliderOptions.min) * scale + logarithmicMinValue))
}

/**
 * Convert the logarithmic value to a non-logarithmic slider position
 * @param {number} value
 * @param {LogarithmicOptions} sliderOptions
 * @param {LogarithmicOptions} logarithmicOptions
 * @returns {number}
 */
export const calculateLogarithmicPosition = (
  value: number,
  sliderOptions: LogarithmicOptions,
  logarithmicOptions?: LogarithmicOptions
): number => {
  const logarithmicMinValue = Math.log(logarithmicOptions?.min || 1)
  const logarithmicMaxValue = Math.log(logarithmicOptions?.max || 1000)

  const scale = (logarithmicMaxValue - logarithmicMinValue) / (sliderOptions.max - sliderOptions.min)
  return Math.round(sliderOptions.min + (Math.log(value) - logarithmicMinValue) / scale)
}
